import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';

import {
  CancelFlowStep,
  PauseDurationValue,
  RetentionOption
} from './constants';
import FinalConfirmationStep from './FinalConfirmationStep';
import PauseDurationStep from './PauseDurationStep';
import RetentionStep from './RetentionStep';
import {
  CancelSubscriptionFlowProps,
  CancelSubscriptionFormData,
  PauseSubscriptionData
} from './types';

const getInitialCancelFormState = (): CancelSubscriptionFormData => ({
  reason: '',
  comment: ''
});

const getInitialPauseState = (): PauseSubscriptionData => ({
  duration: PauseDurationValue.ONE_MONTH,
  pauseUntilDate: null
});

const CancelSubscriptionFlow = ({
  open,
  onClose,
  onConfirmCancel,
  onConfirmPause,
  onNavigateToTraining,
  isLoading,
  subscriptionEndDate
}: CancelSubscriptionFlowProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentStep, setCurrentStep] = useState<CancelFlowStep>(
    CancelFlowStep.RETENTION
  );
  const [selectedOption, setSelectedOption] = useState<RetentionOption>(
    RetentionOption.PAUSE
  );
  const [pauseSubscriptionObj, setPauseSubscriptionObj] =
    useState<PauseSubscriptionData>(getInitialPauseState);
  const [cancelSubscriptionObj, setCancelSubscriptionObj] =
    useState<CancelSubscriptionFormData>(getInitialCancelFormState);

  const dialogTitle = useMemo(() => {
    switch (currentStep) {
      case CancelFlowStep.PAUSE_DURATION:
        return 'Pause Subscription';
      case CancelFlowStep.FINAL_CONFIRMATION:
        return 'Cancel Subscription';
      case CancelFlowStep.RETENTION:
      default:
        return 'Subscription Options';
    }
  }, [currentStep]);

  const resetFlowState = () => {
    setCurrentStep(CancelFlowStep.RETENTION);
    setSelectedOption(RetentionOption.PAUSE);
    setPauseSubscriptionObj(getInitialPauseState());
    setCancelSubscriptionObj(getInitialCancelFormState());
  };

  useEffect(() => {
    if (!open) {
      resetFlowState();
    }
  }, [open]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    resetFlowState();
    onClose();
  };

  const handleRetentionContinue = () => {
    if (selectedOption === RetentionOption.TRAINING) {
      handleClose();
      onNavigateToTraining();
      return;
    }

    if (selectedOption === RetentionOption.CANCEL) {
      setCurrentStep(CancelFlowStep.FINAL_CONFIRMATION);
      return;
    }

    setCurrentStep(CancelFlowStep.PAUSE_DURATION);
  };

  const handlePauseConfirm = async () => {
    const now = dayjs();
    let resumeAt: string | undefined;

    if (pauseSubscriptionObj.duration === PauseDurationValue.ONE_MONTH) {
      resumeAt = now.add(1, 'month').toISOString();
    } else if (pauseSubscriptionObj.duration === PauseDurationValue.TWO_MONTHS) {
      resumeAt = now.add(2, 'month').toISOString();
    } else if (pauseSubscriptionObj.pauseUntilDate) {
      resumeAt = pauseSubscriptionObj.pauseUntilDate
        .hour(now.hour())
        .minute(now.minute())
        .second(now.second())
        .millisecond(now.millisecond())
        .toISOString();
    }

    const isSuccess = await onConfirmPause({ resumeAt });
    if (isSuccess) {
      handleClose();
    }
  };

  const handleConfirmCancel = async () => {
    await onConfirmCancel(cancelSubscriptionObj);
  };

  return (
    <Dialog
      disableEscapeKeyDown={isLoading}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      onClose={(_, reason) => {
        if (
          isLoading &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }

        handleClose();
      }}
      open={open}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pr: 7
        }}
      >
        {dialogTitle}
        <IconButton
          aria-label="close-cancel-subscription-flow"
          disabled={isLoading}
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        {currentStep === CancelFlowStep.RETENTION ? (
          <RetentionStep
            onContinue={handleRetentionContinue}
            onOptionChange={setSelectedOption}
            selectedOption={selectedOption}
          />
        ) : null}

        {currentStep === CancelFlowStep.PAUSE_DURATION ? (
          <PauseDurationStep
            isLoading={isLoading}
            onBack={() => setCurrentStep(CancelFlowStep.RETENTION)}
            onConfirmPause={handlePauseConfirm}
            onPauseUntilDateChange={(value) =>
              setPauseSubscriptionObj((prev) => ({
                ...prev,
                pauseUntilDate: value
              }))
            }
            onDurationChange={(duration) =>
              setPauseSubscriptionObj((prev) => ({
                ...prev,
                duration,
                ...(duration === PauseDurationValue.CUSTOM
                  ? {}
                  : {
                      pauseUntilDate: null
                    })
              }))
            }
            pauseUntilDate={pauseSubscriptionObj.pauseUntilDate}
            selectedDuration={pauseSubscriptionObj.duration}
          />
        ) : null}

        {currentStep === CancelFlowStep.FINAL_CONFIRMATION ? (
          <FinalConfirmationStep
            cancelSubscriptionObj={cancelSubscriptionObj}
            isLoading={isLoading}
            onBack={() => setCurrentStep(CancelFlowStep.RETENTION)}
            onCommentChange={(value) =>
              setCancelSubscriptionObj((prev) => ({
                ...prev,
                comment: value
              }))
            }
            onConfirmCancel={handleConfirmCancel}
            onPauseInstead={() => {
              setSelectedOption(RetentionOption.PAUSE);
              setCurrentStep(CancelFlowStep.PAUSE_DURATION);
            }}
            onReasonChange={(value) =>
              setCancelSubscriptionObj((prev) => ({
                ...prev,
                reason: value
              }))
            }
            subscriptionEndDate={subscriptionEndDate}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CancelSubscriptionFlow;
